import axios from '@fs/zion-axios'
import { getUser } from '@fs/zion-user'
import { getSessionId } from '@fs/zion-session'
import {
  contentCardToThread,
  getContentCards,
  getLastUnreadContentCardTimestamp,
  getShouldLoadContentCards,
} from './contentCards'

export * from './contentCards'

// Not using the default export so that we can add more
// methods to this utility later.
export async function getMessageCounts() {
  const { cisId } = await getUser()
  const url = `/service/messaging/inbox/users/${cisId}/counters`
  return axios.get(url)
}

// check for all kinds of alerts, not just messages.
export async function getAlertsCounts() {
  const { cisId } = await getUser()
  const sessionId = getSessionId()
  const url = `/service/messaging/inbox/users/${cisId}/counters/tags`
  return axios
    .get(url, {
      headers: { Authorization: `bearer ${sessionId}` },
    })
    .then(({ data }) => [
      data.Conversations,
      Object.entries(data).reduce((total, entry) => (entry[0] === 'Conversations' ? total : total + entry[1]), 0),
    ])
}

const messageSummaries = ({ cisId, pageSize, page }) =>
  `/service/messaging/inbox/users/${cisId}/messages/threads?pageSize=${pageSize}&page=${page}`
export function getMessageSummaries({ pageSize, page }) {
  return getUserThreadSummaries({ url: messageSummaries, pageSize, page })
}

const notificationSummaries = ({ cisId, pageSize, page }) =>
  `/service/messaging/inbox/users/${cisId}/notifications/threads?pageSize=${pageSize}&page=${page}`
export async function getNotificationSummaries({ pageSize, page }) {
  // Content cards do not have pagination, so only include them on the first page
  const shouldLoadContentCards = page === 0 && (await getShouldLoadContentCards())
  const [notificationThreadsResponse, contentCards] = await Promise.all([
    getUserThreadSummaries({ url: notificationSummaries, pageSize, page }),
    shouldLoadContentCards ? getContentCards() : Promise.resolve([]),
  ])

  if (contentCards.length > 0) {
    const formattedCards = contentCards.map(contentCardToThread).filter((card) => card.userThreadState !== 'TRASH')
    // Sort combined items by date (newest first)
    const combined = [...(notificationThreadsResponse?.userThreadSummaries || []), ...formattedCards].sort(
      (a, b) => new Date(b.lastModifiedTime) - new Date(a.lastModifiedTime)
    )
    return { ...notificationThreadsResponse, userThreadSummaries: combined }
  }
  return notificationThreadsResponse
}

async function getUserThreadSummaries({ url, pageSize, page }) {
  const { cisId } = await getUser()
  const response = await axios.get(url({ cisId, pageSize, page }))
  return response.data
}

/**
 * Gets an array of notification folder categories the user has muted.
 */
export async function getMutedNotifications() {
  try {
    const { cisId } = await getUser()
    const url = `/service/messaging/inbox/users/${cisId}/notifications/mutes`
    const response = await axios.get(url)
    return response.data
  } catch (error) {
    return []
  }
}

/**
 * Gets the last message and notification update times for the user.
 */
export async function getLastMessageAndNotificationTime() {
  try {
    const shouldLoadContentCards = await getShouldLoadContentCards()
    const contentCardTimestamp = shouldLoadContentCards ? await getLastUnreadContentCardTimestamp() : null
    const { cisId } = await getUser()
    const url = `/service/messaging/inbox/users/${cisId}/updatetimes`
    const response = await axios.get(url)
    if (contentCardTimestamp !== null && contentCardTimestamp > response.data.latest_notification_time) {
      return { ...response.data, latest_notification_time: contentCardTimestamp }
    }
    return response.data
  } catch (error) {
    // fail silently
    return {}
  }
}
